import { ReactNode, useMemo } from 'react'

import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import { cn } from '@/lib/utils'

import { StockColumns, StockItemRow, StockItemsView } from './StockItemRow'

interface StockItemsProps {
  stockItems: StockItem[] | undefined
  isLoading?: boolean
  view?: StockItemsView
  searchQuery?: string
  selectedSkuId?: string
  emptyMessage?: ReactNode
  skuLabel?: (stockItem: StockItem) => ReactNode
  onSelectSku?: (skuId: string) => void
  onAdjusted?: () => void
  onTransfer?: (stockItem: StockItem) => void
  onEditBinLocation?: (stockItem: StockItem) => void
  onShowMoves?: (stockItem: StockItem) => void
}

export const matchesStockQuery = (item: StockItem, query: string) => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [item.skuCode, item.skuName, item.warehouseCode, item.warehouseName, item.batchNumber, item.binLocation]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(q)
}

/** Tabulka zásob se sloupci Rezervováno / Dostupné / Skladem (Shopify inventory). */
export const StockItems = ({
  stockItems,
  isLoading,
  view = 'byWarehouse',
  searchQuery = '',
  selectedSkuId,
  emptyMessage,
  skuLabel,
  onSelectSku,
  onAdjusted,
  onTransfer,
  onEditBinLocation,
  onShowMoves,
}: StockItemsProps) => {
  const filteredItems = useMemo(
    () => (stockItems ?? []).filter((item) => matchesStockQuery(item, searchQuery)),
    [stockItems, searchQuery],
  )

  const columns: StockColumns = {
    sku: view !== 'bySku',
    warehouse: view !== 'byWarehouse',
    batch: (stockItems ?? []).some((item) => item.batchNumber),
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {columns.sku && <TableHead>{view === 'byProduct' ? 'Varianta' : 'Položka'}</TableHead>}
            {columns.warehouse && <TableHead className={cn(columns.sku && 'hidden md:table-cell')}>Sklad</TableHead>}
            <TableHead className="hidden w-28 md:table-cell">Lokace</TableHead>
            {columns.batch && <TableHead className="hidden w-28 md:table-cell">Šarže</TableHead>}
            <TableHead className="hidden w-28 text-right md:table-cell">Rezervováno</TableHead>
            <TableHead className="text-right md:w-36">Dostupné</TableHead>
            <TableHead className="hidden w-24 text-right md:table-cell">Skladem</TableHead>
            <TableHead className="w-px" />
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, index) => <StockItemRow key={index} columns={columns} />)}

          {!isLoading &&
            filteredItems.map((stockItem) => (
              <StockItemRow
                key={stockItem.id}
                stockItem={stockItem}
                columns={columns}
                skuLabel={skuLabel}
                isSelected={!!selectedSkuId && stockItem.skuId === selectedSkuId}
                onSelectSku={onSelectSku}
                onAdjusted={onAdjusted}
                onTransfer={onTransfer}
                onEditBinLocation={onEditBinLocation}
                onShowMoves={onShowMoves}
              />
            ))}
        </TableBody>
      </Table>

      {!isLoading && filteredItems.length === 0 && (
        <p className="px-4 py-10 text-center text-sm text-muted-foreground">
          {emptyMessage ?? (searchQuery.trim() ? 'Hledání neodpovídá žádná zásoba.' : 'Žádné zásoby.')}
        </p>
      )}
    </div>
  )
}
